// Hair is edited via a mask. The card keeps the original photo and copies
// only segmented hair onto it, so the person stays the same.
import { detectFaceForReading } from "./faceapi";
import { CLASS, categoryMask } from "./segmenter";

const LENGTH = {
  "long-straight": "long",
  "shoulder-wave": "bob",
  "short-bob": "bob",
  "short-crop": "short",
  "top-bun": "short",
  "face-frame": "bob",
  "long-wave": "long",
  "layered-lob": "bob",
  "side-swept-bob": "bob",
  "pixie-cut": "short",
  "wispy-bob": "bob",
  "loose-updo": "bob",
  "sleek-back": "short",
  "textured-quiff": "short",
  "crew-cut": "short",
  "curtain-fringe": "bob",
  "wavy-medium": "bob",
  "buzz-cut": "short",
  "neat-taper": "short",
  "side-part": "short",
  "curly-medium": "bob",
  "textured-crop": "short",
};

const memory = new Map();
const pending = new Map();
const queue = [];
let running = 0;
const MAX = 4;
const SHEET_W = 1536;
const SHEET_H = 1024;
const SHEET_COLS = 2;
const SHEET_ROWS = 2;
const FAMILY_VARIANT_COUNT = 4; // leftover sheet helpers only; apply-all uses styles.length
const FRONT_W = 1024;
const FRONT_H = 1536;
const CARD_W = 768;
const CARD_H = 1024;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Gambar tidak bisa dibuka."));
    img.src = src;
  });
}

function layout(pts) {
  const left = pts[0];
  const right = pts[16];
  const chin = pts[8];
  const brow = { x: (pts[19].x + pts[24].x) / 2, y: (pts[19].y + pts[24].y) / 2 };
  return {
    left,
    right,
    chin,
    brow,
    faceH: Math.max(40, chin.y - brow.y),
    faceW: Math.max(40, right.x - left.x),
    cx: (left.x + right.x) / 2,
  };
}

function faceEllipse(pts) {
  const box = layout(pts);
  return {
    cx: box.cx,
    cy: box.brow.y + box.faceH * 0.52,
    rx: box.faceW * 0.48,
    ry: box.faceH * 0.58,
    box,
  };
}

function growPoint(point, cx, cy, scaleX, scaleY) {
  return {
    x: cx + (point.x - cx) * scaleX,
    y: cy + (point.y - cy) * scaleY,
  };
}

function drawFaceSkin(ctx, pts, inflate = 1.12) {
  const box = layout(pts);
  const cx = box.cx;
  const cy = box.brow.y + box.faceH * 0.42;
  const pt = (index) => growPoint(pts[index], cx, cy, inflate, inflate);
  ctx.beginPath();
  ctx.moveTo(pt(0).x, pt(0).y);
  for (let i = 1; i <= 16; i += 1) ctx.lineTo(pt(i).x, pt(i).y);
  for (let i = 26; i >= 17; i -= 1) ctx.lineTo(pt(i).x, pt(i).y);
  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = Math.max(12, box.faceW * 0.07);
  ctx.stroke();
}

function drawEyesMouth(ctx, pts) {
  const left = pointMid(pts[36], pts[39]);
  const right = pointMid(pts[42], pts[45]);
  const eyeR = Math.max(8, Math.hypot(pts[39].x - pts[36].x, pts[39].y - pts[36].y) * 1.05);
  const mouth = pointMid(pts[48], pts[54]);
  const mouthRx = Math.max(10, Math.hypot(pts[54].x - pts[48].x, pts[54].y - pts[48].y) * 0.8);
  ctx.beginPath();
  ctx.ellipse(left.x, left.y, eyeR, eyeR * 0.72, 0, 0, Math.PI * 2);
  ctx.ellipse(right.x, right.y, eyeR, eyeR * 0.72, 0, 0, Math.PI * 2);
  ctx.ellipse(mouth.x, mouth.y, mouthRx, mouthRx * 0.62, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawHairHalo(ctx, pts, width, height) {
  const box = layout(pts);
  const top = box.brow.y - box.faceH * 1.35;
  const bottom = Math.min(height - 1, box.chin.y + box.faceH * 1.65);
  ctx.beginPath();
  ctx.ellipse(
    box.cx,
    (top + bottom) / 2,
    box.faceW * 1.55,
    Math.max(24, (bottom - top) / 2),
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();
}

function drawBangsBand(ctx, pts) {
  const box = layout(pts);
  const top = box.brow.y - box.faceH * 0.52;
  const bottom = box.brow.y + box.faceH * 0.04;
  ctx.beginPath();
  ctx.ellipse(
    box.cx,
    (top + bottom) / 2,
    box.faceW * 0.5,
    Math.max(10, (bottom - top) / 2),
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();
}

function buildHairMask(width, height, points, { bangs = true } = {}) {
  const hair = document.createElement("canvas");
  hair.width = width;
  hair.height = height;
  const ctx = hair.getContext("2d");
  ctx.fillStyle = "#fff";
  drawHairHalo(ctx, points, width, height);
  ctx.globalCompositeOperation = "destination-out";
  drawFaceSkin(ctx, points, 1.14);
  drawEyesMouth(ctx, points);
  if (bangs) {
    ctx.globalCompositeOperation = "source-over";
    drawBangsBand(ctx, points);
    ctx.globalCompositeOperation = "destination-out";
    drawEyesMouth(ctx, points);
  }
  return hair;
}

function buildMaskCanvas(image, points) {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, 0, 0, width, height);
  const hair = buildHairMask(width, height, points, { bangs: true });
  const dest = ctx.getImageData(0, 0, width, height);
  const src = hair.getContext("2d").getImageData(0, 0, width, height).data;
  for (let i = 3; i < dest.data.length; i += 4) {
    dest.data[i] = src[i] > 16 ? 0 : 255;
  }
  ctx.putImageData(dest, 0, 0);
  return canvas;
}

function pump() {
  while (running < MAX && queue.length) {
    running += 1;
    const job = queue.shift();
    job().finally(() => {
      running -= 1;
      pump();
    });
  }
}

function enqueue(job) {
  return new Promise((resolve, reject) => {
    queue.push(() => job().then(resolve, reject));
    pump();
  });
}

async function postPortrait(portrait, fields) {
  const photoBlob = await blobOf(portrait.canvas, "image/jpeg", 0.92);
  const body = new FormData();
  body.append("image", photoBlob, "photo.jpg");
  Object.entries(fields).forEach(([name, value]) => body.append(name, value));
  const res = await fetch("/api/hair-edit", { method: "POST", body });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Rambut tidak terpasang.");
  }
  return presentGenerated(await blobToDataUrl(await res.blob()));
}

async function editOnce({ photoUrl, points, style, color }) {
  const original = await loadImage(photoUrl);
  const portrait = composePortrait(original, points, FRONT_W, FRONT_H);
  return postPortrait(portrait, { style: style.id, color: color || "" });
}

function sheetKind(styleIds) {
  return styleIds.map((id) => LENGTH[id] || "long");
}

function hairBox(points, kind, width, height) {
  const face = faceEllipse(points);
  const { box } = face;
  const side = box.faceW * 1.7;
  const top = box.brow.y - box.faceH * 1.35;
  const bottom = Math.min(height - 1, box.chin.y + box.faceH * 1.7);
  return {
    face,
    hx: box.cx,
    hy: (top + bottom) / 2,
    hrx: side,
    hry: Math.max(20, (bottom - top) / 2),
  };
}

function profileHairBox(points) {
  const face = faceEllipse(points);
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const faceW = Math.max(36, maxX - minX);
  const faceH = Math.max(48, maxY - minY);
  const centerX = (minX + maxX) / 2;
  const noseDirection = Math.sign((points[30]?.x || centerX) - centerX) || 1;
  const top = minY - faceH * 1.1;
  const bottom = maxY + faceH * 0.5;
  return {
    face: {
      ...face,
      rx: face.rx * 0.82,
      ry: face.ry * 0.9,
    },
    hx: centerX - noseDirection * faceW * 0.38,
    hy: (top + bottom) / 2,
    hrx: faceW * 1.65,
    hry: Math.max(30, (bottom - top) / 2),
  };
}

function lookbookCrop(points, srcW, srcH, aspect = 4 / 5) {
  const box = layout(points);
  let cropW = Math.max(box.faceW * 2.35, srcW * 0.42);
  let cropH = cropW / aspect;
  if (cropH < box.faceH * 3.05) {
    cropH = box.faceH * 3.05;
    cropW = cropH * aspect;
  }
  let left = box.cx - cropW / 2;
  let top = box.brow.y - cropH * 0.34;
  if (left < 0) left = 0;
  if (top < 0) top = 0;
  if (left + cropW > srcW) left = Math.max(0, srcW - cropW);
  if (top + cropH > srcH) top = Math.max(0, srcH - cropH);
  cropW = Math.min(cropW, srcW - left);
  cropH = Math.min(cropH, srcH - top);
  return { left, top, width: Math.max(40, cropW), height: Math.max(40, cropH) };
}

function composePortrait(image, points, outW, outH) {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  const crop = lookbookCrop(points, srcW, srcH, outW / outH);
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  const scale = Math.max(outW / crop.width, outH / crop.height);
  const dw = crop.width * scale;
  const dh = crop.height * scale;
  const dx = (outW - dw) / 2;
  const dy = (outH - dh) / 2;
  ctx.drawImage(image, crop.left, crop.top, crop.width, crop.height, dx, dy, dw, dh);
  return {
    canvas,
    points: points.map((point) => ({
      x: (point.x - crop.left) * scale + dx,
      y: (point.y - crop.top) * scale + dy,
    })),
  };
}

function imageToCanvas(image, width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(
    image,
    0,
    0,
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    0,
    0,
    width,
    height,
  );
  return canvas;
}

async function alignGenerated(generated, basePoints, width, height) {
  const fitted = imageToCanvas(generated, width, height);
  let faces = [];
  try {
    faces = await detectFaceForReading(fitted);
  } catch (err) {
    faces = [];
  }
  if (!faces.length) return fitted;
  const face = faces.reduce((a, b) => (
    a.detection.box.width * a.detection.box.height > b.detection.box.width * b.detection.box.height ? a : b
  ));
  const pts = face.landmarks.positions;
  const genLeft = pointMid(pts[36], pts[39]);
  const genRight = pointMid(pts[42], pts[45]);
  const baseLeft = pointMid(basePoints[36], basePoints[39]);
  const baseRight = pointMid(basePoints[42], basePoints[45]);
  const genMid = pointMid(genLeft, genRight);
  const baseMid = pointMid(baseLeft, baseRight);
  const genSpan = Math.hypot(genRight.x - genLeft.x, genRight.y - genLeft.y) || 1;
  const baseSpan = Math.hypot(baseRight.x - baseLeft.x, baseRight.y - baseLeft.y) || 1;
  const scale = baseSpan / genSpan;
  const angle = Math.atan2(baseRight.y - baseLeft.y, baseRight.x - baseLeft.x)
    - Math.atan2(genRight.y - genLeft.y, genRight.x - genLeft.x);
  if (scale < 0.7 || scale > 1.45 || Math.abs(angle) > 0.35) return fitted;
  const aligned = document.createElement("canvas");
  aligned.width = width;
  aligned.height = height;
  const ctx = aligned.getContext("2d");
  ctx.fillStyle = sampleBg(fitted);
  ctx.fillRect(0, 0, width, height);
  ctx.translate(baseMid.x, baseMid.y);
  ctx.rotate(angle);
  ctx.scale(scale, scale);
  ctx.translate(-genMid.x, -genMid.y);
  ctx.drawImage(fitted, 0, 0);
  return aligned;
}

function blurAlpha(source) {
  const out = document.createElement("canvas");
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext("2d");
  ctx.filter = "blur(5px)";
  ctx.drawImage(source, 0, 0);
  ctx.filter = "none";
  return out;
}

function paintClassMask(seg, pred, destW, destH) {
  const raw = document.createElement("canvas");
  raw.width = seg.width;
  raw.height = seg.height;
  const ctx = raw.getContext("2d");
  const img = ctx.createImageData(seg.width, seg.height);
  for (let i = 0; i < seg.data.length; i += 1) {
    const on = pred(seg.data[i]);
    const j = i * 4;
    img.data[j] = 255;
    img.data[j + 1] = 255;
    img.data[j + 2] = 255;
    img.data[j + 3] = on ? 255 : 0;
  }
  ctx.putImageData(img, 0, 0);
  const out = document.createElement("canvas");
  out.width = destW;
  out.height = destH;
  out.getContext("2d").drawImage(raw, 0, 0, destW, destH);
  return out;
}

async function compositeHair(base, basePoints, generated) {
  const width = base.width;
  const height = base.height;
  const aligned = await alignGenerated(generated, basePoints, width, height);
  const hairGeo = buildHairMask(width, height, basePoints, { bangs: false });
  const bangsGeo = document.createElement("canvas");
  bangsGeo.width = width;
  bangsGeo.height = height;
  const bangsCtx = bangsGeo.getContext("2d");
  bangsCtx.fillStyle = "#fff";
  drawBangsBand(bangsCtx, basePoints);
  bangsCtx.globalCompositeOperation = "destination-out";
  drawEyesMouth(bangsCtx, basePoints);

  const alpha = document.createElement("canvas");
  alpha.width = width;
  alpha.height = height;
  const actx = alpha.getContext("2d");
  try {
    const [origSeg, genSeg] = await Promise.all([categoryMask(base), categoryMask(aligned)]);
    const genHair = paintClassMask(genSeg, (cls) => cls === CLASS.HAIR, width, height);
    const origFace = paintClassMask(origSeg, (cls) => cls === CLASS.FACE, width, height);
    const origBody = paintClassMask(
      origSeg,
      (cls) => cls === CLASS.BODY || cls === CLASS.CLOTHES,
      width,
      height,
    );
    actx.drawImage(genHair, 0, 0);
    actx.globalCompositeOperation = "destination-in";
    const gate = document.createElement("canvas");
    gate.width = width;
    gate.height = height;
    const gctx = gate.getContext("2d");
    gctx.drawImage(hairGeo, 0, 0);
    gctx.drawImage(bangsGeo, 0, 0);
    actx.drawImage(gate, 0, 0);
    actx.globalCompositeOperation = "destination-out";
    actx.drawImage(origBody, 0, 0);
    const faceKeep = document.createElement("canvas");
    faceKeep.width = width;
    faceKeep.height = height;
    const fctx = faceKeep.getContext("2d");
    fctx.drawImage(origFace, 0, 0);
    fctx.globalCompositeOperation = "destination-out";
    fctx.drawImage(bangsGeo, 0, 0);
    actx.drawImage(faceKeep, 0, 0);
    const painted = actx.getImageData(0, 0, width, height).data;
    let hairPixels = 0;
    for (let i = 3; i < painted.length; i += 4) {
      if (painted[i] > 20) hairPixels += 1;
    }
    if (hairPixels < width * height * 0.02) {
      actx.globalCompositeOperation = "source-over";
      actx.clearRect(0, 0, width, height);
      actx.drawImage(hairGeo, 0, 0);
    }
  } catch (err) {
    actx.globalCompositeOperation = "source-over";
    actx.clearRect(0, 0, width, height);
    actx.drawImage(hairGeo, 0, 0);
  }

  const soft = blurAlpha(alpha);
  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const ctx = out.getContext("2d");
  ctx.drawImage(base, 0, 0);
  const dest = ctx.getImageData(0, 0, width, height);
  const src = aligned.getContext("2d").getImageData(0, 0, width, height).data;
  const mix = soft.getContext("2d").getImageData(0, 0, width, height).data;
  const pix = dest.data;
  for (let i = 0; i < pix.length; i += 4) {
    const a = mix[i + 3] / 255;
    if (a < 0.03) continue;
    pix[i] = src[i] * a + pix[i] * (1 - a);
    pix[i + 1] = src[i + 1] * a + pix[i + 1] * (1 - a);
    pix[i + 2] = src[i + 2] * a + pix[i + 2] * (1 - a);
  }
  ctx.putImageData(dest, 0, 0);
  return out;
}

async function presentGenerated(source) {
  const image = typeof source === "string" ? await loadImage(source) : source;
  return fitCanvas(image, CARD_W, CARD_H).toDataURL("image/jpeg", 0.94);
}

async function lockIdentity(baseCanvas, basePoints, generatedSource) {
  const generated = typeof generatedSource === "string"
    ? await loadImage(generatedSource)
    : generatedSource;
  const blended = await compositeHair(baseCanvas, basePoints, generated);
  return fitCanvas(blended, CARD_W, CARD_H).toDataURL("image/jpeg", 0.94);
}

function sampleBg(image) {
  const probe = document.createElement("canvas");
  probe.width = 1;
  probe.height = 1;
  const ctx = probe.getContext("2d");
  ctx.drawImage(image, 0, 0, 1, 1, 0, 0, 1, 1);
  const pix = ctx.getImageData(0, 0, 1, 1).data;
  return `rgb(${pix[0]}, ${pix[1]}, ${pix[2]})`;
}

function fitCanvas(image, outW, outH) {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  const scale = Math.max(outW / srcW, outH / srcH);
  const dw = srcW * scale;
  const dh = srcH * scale;
  ctx.drawImage(image, 0, 0, srcW, srcH, (outW - dw) / 2, (outH - dh) * 0.18, dw, dh);
  return canvas;
}

function mapPoint(point, mid, cos, sin, pad) {
  const dx = point.x - mid.x;
  const dy = point.y - mid.y;
  return {
    x: dx * cos - dy * sin + mid.x + pad,
    y: dx * sin + dy * cos + mid.y + pad,
  };
}

async function straightenAndFrame(panel) {
  const srcW = panel.naturalWidth || panel.width;
  const srcH = panel.naturalHeight || panel.height;
  let faces = [];
  try {
    faces = await detectFaceForReading(panel);
  } catch (err) {
    faces = [];
  }
  if (!faces.length) return fitCanvas(panel, CARD_W, CARD_H);
  const face = faces.reduce((a, b) => (
    a.detection.box.width * a.detection.box.height > b.detection.box.width * b.detection.box.height ? a : b
  ));
  const pts = face.landmarks.positions.map((point) => ({ x: point.x, y: point.y }));
  const left = pointMid(pts[36], pts[39]);
  const right = pointMid(pts[42], pts[45]);
  const angle = Math.atan2(right.y - left.y, right.x - left.x);
  const mid = pointMid(left, right);
  if (Math.abs(angle) < 0.008) {
    return composePortrait(panel, pts, CARD_W, CARD_H).canvas;
  }
  const pad = Math.round(Math.max(srcW, srcH) * 0.35);
  const rotated = document.createElement("canvas");
  rotated.width = srcW + pad * 2;
  rotated.height = srcH + pad * 2;
  const ctx = rotated.getContext("2d");
  ctx.fillStyle = sampleBg(panel);
  ctx.fillRect(0, 0, rotated.width, rotated.height);
  ctx.translate(mid.x + pad, mid.y + pad);
  ctx.rotate(-angle);
  ctx.drawImage(panel, -mid.x, -mid.y);
  const cos = Math.cos(-angle);
  const sin = Math.sin(-angle);
  const mapped = pts.map((point) => mapPoint(point, mid, cos, sin, pad));
  return composePortrait(rotated, mapped, CARD_W, CARD_H).canvas;
}

async function frameResult(raw) {
  const image = await loadImage(raw);
  const framed = await straightenAndFrame(image);
  return framed.toDataURL("image/jpeg", 0.94);
}

function headRegion(points, srcW, srcH) {
  const box = layout(points);
  const left = Math.max(0, box.cx - box.faceW * 1.45);
  const right = Math.min(srcW, box.cx + box.faceW * 1.45);
  const top = Math.max(0, box.brow.y - box.faceH * 1.45);
  const bottom = Math.min(srcH, box.chin.y + box.faceH * 1.25);
  return {
    left,
    top,
    right: Math.max(left + 40, right),
    bottom: Math.max(top + 40, bottom),
  };
}

function profileHeadRegion(points, srcW, srcH) {
  const box = layout(points);
  const noseDir = Math.sign((points[30]?.x || box.cx) - box.cx) || 1;
  const frontPad = box.faceW * 0.95;
  const backPad = box.faceW * 1.85;
  const left = Math.max(0, noseDir > 0 ? box.cx - backPad : box.cx - frontPad);
  const right = Math.min(srcW, noseDir > 0 ? box.cx + frontPad : box.cx + backPad);
  const top = Math.max(0, box.brow.y - box.faceH * 1.55);
  const bottom = Math.min(srcH, box.chin.y + box.faceH * 0.85);
  return {
    left,
    top,
    right: Math.max(left + 40, right),
    bottom: Math.max(top + 40, bottom),
  };
}

function buildSheet(image, points) {
  const panelW = Math.round(SHEET_W / SHEET_COLS);
  const panelH = Math.round(SHEET_H / SHEET_ROWS);
  const portrait = composePortrait(image, points, panelW, panelH);
  const canvas = document.createElement("canvas");
  canvas.width = SHEET_W;
  canvas.height = SHEET_H;
  const ctx = canvas.getContext("2d");
  for (let i = 0; i < FAMILY_VARIANT_COUNT; i += 1) {
    const x = (i % SHEET_COLS) * panelW;
    const y = Math.floor(i / SHEET_COLS) * panelH;
    ctx.drawImage(portrait.canvas, x, y);
  }
  const mask = document.createElement("canvas");
  mask.width = SHEET_W;
  mask.height = SHEET_H;
  const mctx = mask.getContext("2d");
  mctx.drawImage(canvas, 0, 0);
  const data = mctx.getImageData(0, 0, mask.width, mask.height);
  const pix = data.data;
  const hair = buildHairMask(panelW, panelH, portrait.points, { bangs: true });
  const hairPix = hair.getContext("2d").getImageData(0, 0, panelW, panelH).data;
  for (let i = 0; i < FAMILY_VARIANT_COUNT; i += 1) {
    const ox = (i % SHEET_COLS) * panelW;
    const oy = Math.floor(i / SHEET_COLS) * panelH;
    for (let y = 0; y < panelH; y += 1) {
      for (let x = 0; x < panelW; x += 1) {
        if (hairPix[(y * panelW + x) * 4 + 3] > 16) {
          pix[((oy + y) * mask.width + (ox + x)) * 4 + 3] = 0;
        }
      }
    }
  }
  mctx.putImageData(data, 0, 0);
  return { canvas, mask, portrait, panelW, panelH };
}

function drawCoverPanel(ctx, image, points, crop, index, pw, ph) {
  const cropW = crop.right - crop.left;
  const cropH = crop.bottom - crop.top;
  const scale = Math.max(pw / cropW, ph / cropH);
  const dw = cropW * scale;
  const dh = cropH * scale;
  const dx = index * pw + (pw - dw) / 2;
  const dy = (ph - dh) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.rect(index * pw, 0, pw, ph);
  ctx.clip();
  ctx.drawImage(image, crop.left, crop.top, cropW, cropH, dx, dy, dw, dh);
  ctx.restore();
  return points.map((point) => ({
    x: (point.x - crop.left) * scale + dx,
    y: (point.y - crop.top) * scale + dy,
  }));
}

function buildViewSheet(frontImage, frontPoints, sideImage, sidePoints) {
  const frontW = frontImage.naturalWidth || frontImage.width;
  const frontH = frontImage.naturalHeight || frontImage.height;
  const sideW = sideImage.naturalWidth || sideImage.width;
  const sideH = sideImage.naturalHeight || sideImage.height;
  const frontCrop = headRegion(frontPoints, frontW, frontH);
  const sideCrop = profileHeadRegion(sidePoints, sideW, sideH);
  const pw = 300;
  const ph = 360;
  const canvas = document.createElement("canvas");
  canvas.width = pw * 3;
  canvas.height = ph;
  const ctx = canvas.getContext("2d");
  const transformed = [
    drawCoverPanel(ctx, frontImage, frontPoints, frontCrop, 0, pw, ph),
    drawCoverPanel(ctx, frontImage, frontPoints, frontCrop, 1, pw, ph),
    drawCoverPanel(ctx, sideImage, sidePoints, sideCrop, 2, pw, ph),
  ];

  const mask = document.createElement("canvas");
  mask.width = canvas.width;
  mask.height = canvas.height;
  const mctx = mask.getContext("2d");
  mctx.drawImage(canvas, 0, 0);
  const data = mctx.getImageData(0, 0, mask.width, mask.height);
  const pix = data.data;

  for (let index = 0; index < 3; index += 1) {
    const ox = index * pw;
    const shifted = transformed[index];
    const box = layout(shifted);
    const region = index === 2
      ? profileHairBox(shifted)
      : hairBox(shifted, "long", mask.width, mask.height);
    const poseTop = box.brow.y - box.faceH * 1.35;
    const poseBottom = box.chin.y + box.faceH * 0.35;
    const poseCy = (poseTop + poseBottom) / 2;
    const poseRy = Math.max(30, (poseBottom - poseTop) / 2);
    const poseRx = box.faceW * 1.12;
    for (let y = 0; y < ph; y += 1) {
      for (let x = ox; x < ox + pw; x += 1) {
        const offset = (y * mask.width + x) * 4 + 3;
        if (index !== 1) {
          const inHair = ((x - region.hx) / region.hrx) ** 2 + ((y - region.hy) / region.hry) ** 2 <= 1;
          const inFace = ((x - region.face.cx) / region.face.rx) ** 2 + ((y - region.face.cy) / region.face.ry) ** 2 <= 1;
          if (inHair && !inFace) pix[offset] = 0;
        } else {
          const inPose = ((x - box.cx) / poseRx) ** 2 + ((y - poseCy) / poseRy) ** 2 <= 1;
          if (inPose) pix[offset] = 0;
        }
      }
    }
  }
  mctx.putImageData(data, 0, 0);
  return { canvas, mask, transformed };
}

function buildProfileSheet(image, points) {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  const crop = profileHeadRegion(points, srcW, srcH);
  const pw = 300;
  const ph = 360;
  const canvas = document.createElement("canvas");
  canvas.width = pw;
  canvas.height = ph;
  const ctx = canvas.getContext("2d");
  const shifted = drawCoverPanel(ctx, image, points, crop, 0, pw, ph);
  const mask = document.createElement("canvas");
  mask.width = pw;
  mask.height = ph;
  const mctx = mask.getContext("2d");
  mctx.drawImage(canvas, 0, 0);
  const data = mctx.getImageData(0, 0, mask.width, mask.height);
  const pix = data.data;
  const region = profileHairBox(shifted);
  for (let y = 0; y < ph; y += 1) {
    for (let x = 0; x < pw; x += 1) {
      const inHair = ((x - region.hx) / region.hrx) ** 2 + ((y - region.hy) / region.hry) ** 2 <= 1;
      const inFace = ((x - region.face.cx) / region.face.rx) ** 2 + ((y - region.face.cy) / region.face.ry) ** 2 <= 1;
      if (inHair && !inFace) pix[(y * mask.width + x) * 4 + 3] = 0;
    }
  }
  mctx.putImageData(data, 0, 0);
  return { canvas, mask };
}

async function postHairEdit(canvas, mask, fields) {
  const photoBlob = await blobOf(canvas, "image/jpeg", 0.9);
  const maskBlob = await blobOf(mask, "image/png");
  const body = new FormData();
  body.append("image", photoBlob, "photo.jpg");
  body.append("mask", maskBlob, "mask.png");
  Object.entries(fields).forEach(([key, value]) => body.append(key, value));
  const res = await fetch("/api/hair-edit", { method: "POST", body });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Rambut tidak terpasang.");
  }
  return blobToDataUrl(await res.blob());
}

async function stitchProfilePanel(viewsUrl, profileUrl) {
  const views = await loadImage(viewsUrl);
  const profile = await loadImage(profileUrl);
  const canvas = document.createElement("canvas");
  canvas.width = views.naturalWidth || views.width;
  canvas.height = views.naturalHeight || views.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(views, 0, 0, canvas.width, canvas.height);
  const pw = canvas.width / 3;
  const ph = canvas.height;
  const srcW = profile.naturalWidth || profile.width;
  const srcH = profile.naturalHeight || profile.height;
  const scale = Math.max(pw / srcW, ph / srcH);
  const dw = srcW * scale;
  const dh = srcH * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(pw * 2, 0, pw, ph);
  ctx.clip();
  ctx.drawImage(profile, 0, 0, srcW, srcH, pw * 2 + (pw - dw) / 2, (ph - dh) / 2, dw, dh);
  ctx.restore();
  return canvas.toDataURL("image/jpeg", 0.94);
}

function blobOf(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Hasil tidak bisa dibaca."));
    reader.readAsDataURL(blob);
  });
}

function pointMid(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

async function restoreSheetIdentity(resultUrl, original, sourcePoints, count, restoreIndices = null, grid = null) {
  const result = await loadImage(resultUrl);
  const canvas = document.createElement("canvas");
  canvas.width = result.naturalWidth || result.width;
  canvas.height = result.naturalHeight || result.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(result, 0, 0, canvas.width, canvas.height);
  const cols = grid?.cols || count;
  const rows = grid?.rows || Math.ceil(count / cols);
  const panelWidth = canvas.width / cols;
  const panelHeight = canvas.height / rows;
  let restored = 0;

  for (let index = 0; index < count; index += 1) {
    if (restoreIndices && !restoreIndices.includes(index)) continue;
    const col = index % cols;
    const row = Math.floor(index / cols);
    const x0 = Math.round(col * panelWidth);
    const x1 = Math.round((col + 1) * panelWidth);
    const y0 = Math.round(row * panelHeight);
    const y1 = Math.round((row + 1) * panelHeight);
    const panel = document.createElement("canvas");
    panel.width = Math.max(1, x1 - x0);
    panel.height = Math.max(1, y1 - y0);
    panel.getContext("2d").drawImage(canvas, x0, y0, panel.width, panel.height, 0, 0, panel.width, panel.height);
    const faces = await detectFaceForReading(panel);
    if (!faces.length) continue;
    const face = faces.reduce((a, b) => (
      a.detection.box.width * a.detection.box.height > b.detection.box.width * b.detection.box.height ? a : b
    ));
    const target = face.landmarks.positions.map((point) => ({ x: point.x + x0, y: point.y + y0 }));
    const sourceLeftEye = pointMid(sourcePoints[36], sourcePoints[39]);
    const sourceRightEye = pointMid(sourcePoints[42], sourcePoints[45]);
    const targetLeftEye = pointMid(target[36], target[39]);
    const targetRightEye = pointMid(target[42], target[45]);
    const sourceMid = pointMid(sourceLeftEye, sourceRightEye);
    const targetMid = pointMid(targetLeftEye, targetRightEye);
    const sourceDx = sourceRightEye.x - sourceLeftEye.x;
    const sourceDy = sourceRightEye.y - sourceLeftEye.y;
    const targetDx = targetRightEye.x - targetLeftEye.x;
    const targetDy = targetRightEye.y - targetLeftEye.y;
    const sourceDistance = Math.hypot(sourceDx, sourceDy) || 1;
    const scale = Math.hypot(targetDx, targetDy) / sourceDistance;
    const angle = Math.atan2(targetDy, targetDx) - Math.atan2(sourceDy, sourceDx);
    if (scale < 0.84 || scale > 1.18 || Math.abs(angle) > 0.1) continue;

    const overlay = document.createElement("canvas");
    overlay.width = canvas.width;
    overlay.height = canvas.height;
    const overlayCtx = overlay.getContext("2d");
    overlayCtx.translate(targetMid.x, targetMid.y);
    overlayCtx.rotate(angle);
    overlayCtx.scale(scale, scale);
    overlayCtx.translate(-sourceMid.x, -sourceMid.y);
    overlayCtx.drawImage(original, 0, 0);
    overlayCtx.setTransform(1, 0, 0, 1, 0, 0);

    const mask = document.createElement("canvas");
    mask.width = canvas.width;
    mask.height = canvas.height;
    const maskCtx = mask.getContext("2d");
    const faceWidth = Math.hypot(target[16].x - target[0].x, target[16].y - target[0].y);
    const eyeMid = pointMid(targetLeftEye, targetRightEye);
    const mouth = pointMid(target[48], target[54]);
    const nose = target[30];
    maskCtx.filter = `blur(${Math.max(8, faceWidth * 0.1)}px)`;
    maskCtx.fillStyle = "#fff";
    maskCtx.beginPath();
    maskCtx.ellipse(eyeMid.x, nose.y, faceWidth * 0.28, Math.max(18, (mouth.y - eyeMid.y) * 1.05), 0, 0, Math.PI * 2);
    maskCtx.fill();
    maskCtx.filter = "none";
    overlayCtx.globalCompositeOperation = "destination-in";
    overlayCtx.drawImage(mask, 0, 0);
    overlayCtx.globalCompositeOperation = "source-over";
    ctx.drawImage(overlay, 0, 0);
    restored += 1;
  }

  return restored ? canvas.toDataURL("image/jpeg", 0.94) : resultUrl;
}

async function splitSheet(resultUrl, count = FAMILY_VARIANT_COUNT, cols = SHEET_COLS, rows = SHEET_ROWS) {
  const result = await loadImage(resultUrl);
  const width = result.naturalWidth || result.width;
  const height = result.naturalHeight || result.height;
  const panelW = width / cols;
  const panelH = height / rows;
  const insetX = panelW * 0.012;
  const insetY = panelH * 0.012;
  const panels = [];
  for (let i = 0; i < count; i += 1) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const sx = Math.max(0, Math.round(col * panelW + insetX));
    const sy = Math.max(0, Math.round(row * panelH + insetY));
    const sw = Math.max(1, Math.round(panelW - insetX * 2));
    const sh = Math.max(1, Math.round(panelH - insetY * 2));
    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    canvas.getContext("2d").drawImage(result, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    panels.push(canvas);
  }
  return panels;
}

async function editSheet({ photoUrl, points, styles }) {
  const urls = await Promise.all(styles.map((style) => requestHairFront({ photoUrl, points, style })));
  return Object.fromEntries(styles.map((style, index) => [style.id, urls[index]]));
}

async function editViews({ photoUrl, points, sidePhotoUrl, sidePoints, style }) {
  const original = await loadImage(photoUrl);
  const sideOriginal = await loadImage(sidePhotoUrl);
  const viewSheet = buildViewSheet(original, points, sideOriginal, sidePoints);
  const profileSheet = buildProfileSheet(sideOriginal, sidePoints);
  const viewsPromise = postHairEdit(viewSheet.canvas, viewSheet.mask, { mode: "views", style: style.id });
  const profilePromise = postHairEdit(profileSheet.canvas, profileSheet.mask, { mode: "profile", style: style.id }).catch(() => null);
  const viewsRaw = await viewsPromise;
  const profileRaw = await profilePromise;
  // Keep the original front face. Never paste frontal landmarks onto the
  // profile; that warps the nose, ear, and jaw.
  const frontLocked = await restoreSheetIdentity(viewsRaw, original, points, 3, [0]).catch(() => viewsRaw);
  return profileRaw ? stitchProfilePanel(frontLocked, profileRaw) : frontLocked;
}

export function requestHairViews(args) {
  if (
    !args.photoUrl || !args.points || args.points.length < 68
    || !args.sidePhotoUrl || !args.sidePoints || args.sidePoints.length < 68
    || !args.style?.id
  ) {
    return Promise.reject(new Error("Foto depan dan samping belum siap."));
  }
  const key = `views5|${args.photoUrl.length}:${args.photoUrl.slice(-48)}|${args.sidePhotoUrl.length}:${args.sidePhotoUrl.slice(-48)}|${args.style.id}`;
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (pending.has(key)) return pending.get(key);
  const job = enqueue(() => editViews(args)).then((url) => {
    memory.set(key, url);
    pending.delete(key);
    return url;
  }).catch((err) => {
    pending.delete(key);
    throw err;
  });
  pending.set(key, job);
  return job;
}

async function editColorOnly({ photoUrl, points, style, color }) {
  const original = await loadImage(photoUrl);
  const portrait = composePortrait(original, points, FRONT_W, FRONT_H);
  return postPortrait(portrait, { mode: "color-only", style: style.id, color });
}

async function composeColorGrid(urls) {
  const images = await Promise.all(urls.map(loadImage));
  const pw = Math.max(...images.map((image) => image.naturalWidth || image.width));
  const ph = Math.max(...images.map((image) => image.naturalHeight || image.height));
  const gap = Math.max(8, Math.round(pw * 0.025));
  const canvas = document.createElement("canvas");
  canvas.width = pw * 3 + gap * 4;
  canvas.height = ph * 2 + gap * 3;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  images.forEach((image, index) => {
    const x = gap + (index % 3) * (pw + gap);
    const y = gap + Math.floor(index / 3) * (ph + gap);
    ctx.drawImage(image, x, y, pw, ph);
  });
  return canvas.toDataURL("image/jpeg", 0.94);
}

export function requestHairFront(args) {
  if (!args.photoUrl || !args.points || args.points.length < 68 || !args.style?.id) {
    return Promise.reject(new Error("Foto depan belum siap."));
  }
  const key = `front-snap8|${args.photoUrl.length}:${args.photoUrl.slice(-48)}|${args.style.id}`;
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (pending.has(key)) return pending.get(key);
  const job = enqueue(() => editFront(args)).then((url) => {
    memory.set(key, url);
    pending.delete(key);
    return url;
  }).catch((err) => {
    pending.delete(key);
    throw err;
  });
  pending.set(key, job);
  return job;
}

async function editFront({ photoUrl, points, style }) {
  const original = await loadImage(photoUrl);
  const portrait = composePortrait(original, points, FRONT_W, FRONT_H);
  return postPortrait(portrait, { mode: "front", style: style.id });
}

export function requestHairColor({ photoUrl, points, style, color }) {
  if (!photoUrl || !points || points.length < 68 || !style?.id || !color) {
    return Promise.reject(new Error("Hasil model dan warna belum siap."));
  }
  const key = `color-snap2|${photoUrl.length}:${photoUrl.slice(-48)}|${style.id}|${color}`;
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (pending.has(key)) return pending.get(key);
  const job = enqueue(() => editColorOnly({ photoUrl, points, style, color })).then((url) => {
    memory.set(key, url);
    pending.delete(key);
    return url;
  }).catch((err) => {
    pending.delete(key);
    throw err;
  });
  pending.set(key, job);
  return job;
}

export async function requestHairColors({ photoUrl, points, style, colors }) {
  if (
    !photoUrl || !points || points.length < 68 || !style?.id
    || !Array.isArray(colors) || colors.length !== 6
  ) {
    throw new Error("Hasil model dan enam warna belum siap.");
  }
  return Promise.all(colors.map((color) => requestHairColor({ photoUrl, points, style, color })));
}

export function requestHairSheet(args) {
  const ids = (args.styles || []).map((style) => style.id);
  if (!args.photoUrl || !args.points || args.points.length < 68 || ids.length < 1) {
    return Promise.reject(new Error("Foto belum siap."));
  }
  const colorKey = (args.colors || []).join(",");
  const key = `sheet-snap9|${args.photoUrl.length}:${args.photoUrl.slice(-48)}|${ids.join(",")}|${colorKey}`;
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (pending.has(key)) return pending.get(key);
  const job = enqueue(() => editSheet(args)).then((url) => {
    memory.set(key, url);
    pending.delete(key);
    return url;
  }).catch((err) => {
    pending.delete(key);
    throw err;
  });
  pending.set(key, job);
  return job;
}

export async function requestFaceAnalysis(photoUrl, gender = "wanita") {
  if (!photoUrl) throw new Error("Foto belum siap.");
  const key = `analysis2|${gender}|${photoUrl.length}:${photoUrl.slice(-48)}`;
  if (memory.has(key)) return memory.get(key);
  const blob = await (await fetch(photoUrl)).blob();
  const body = new FormData();
  body.append("image", blob, "photo.jpg");
  body.append("mode", "analysis");
  body.append("gender", gender);
  const res = await fetch("/api/hair-edit", { method: "POST", body });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Analisa AI sedang tidak tersedia.");
  }
  const result = await res.json();
  memory.set(key, result);
  return result;
}

export function requestHairEdit(args) {
  if (!args.photoUrl || !args.points || args.points.length < 68 || !args.style?.id || !args.color) {
    return Promise.reject(new Error("Foto belum siap."));
  }
  const key = `v9|${args.photoUrl.length}:${args.photoUrl.slice(-48)}|${args.style.id}|${args.color}`;
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (pending.has(key)) return pending.get(key);
  const job = enqueue(() => editOnce(args)).then((url) => {
    memory.set(key, url);
    pending.delete(key);
    return url;
  }).catch((err) => {
    pending.delete(key);
    throw err;
  });
  pending.set(key, job);
  return job;
}
